import { Tag, Pencil } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';


import { useProviderCompany } from "../../../../../context/ProviderCompanyContext";
import { updateProviderOffersPreferences } from '../../../../../services/staffingService';
import OfferDisputeRules from './rules/offerdisputerules';

// Define validation schema using Yup
const offersSchema = yup.object().shape({
    offerExpiry: yup.number().min(1).max(24).required('Offer expiry is required'),
});

const OffersDisputes = ({ providerCompanyId }) => {
    const { providerCompanyData, fetchProviderCompanyData } = useProviderCompany();
    const [isEditing, setIsEditing] = useState(false);

    const [disputeRules, setDisputeRules] = useState([
        { entity: 'shift', status: 1, definition: 'No-show for scheduled shift', editable: false },
        { entity: 'shift', status: 2, definition: 'Late arrival to scheduled shift', editable: false },
        { entity: 'shift', status: 3, definition: 'Missing checkout at shift end', editable: false },
        { entity: 'shift', status: 4, definition: 'Late check-in for shift', editable: false },
        { entity: 'shift', status: 5, definition: 'Check-in or check-out outside geofence area', editable: false },
        { entity: 'shift', status: 6, definition: 'Inappropriate behavior during shift', editable: false }
    ]);

    const { register, handleSubmit, setValue, formState: { errors }, reset } = useForm({
        resolver: yupResolver(offersSchema),
        defaultValues: {
            offerExpiry: 0,
        },
    });

    useEffect(() => {
        if (providerCompanyData && providerCompanyData.preferences?.offers) {
            reset({
                offerExpiry: providerCompanyData.preferences.offers.officerResponseTimeHours || 0,
            });
        }
    }, [providerCompanyData, reset]);

    const onSubmit = async (data) => {
        try {
            const offerData = {
                officerResponseTimeHours: data.offerExpiry,
                disputeRules
            };

            console.log("Dispute Rules:", disputeRules);

            await updateProviderOffersPreferences(providerCompanyData._id, offerData);
            fetchProviderCompanyData(providerCompanyId);
            setIsEditing(false);
        } catch (error) {
            console.error("Failed to update offers preferences:", error);
        }
    };

    const toggleEditMode = () => {
        setIsEditing(!isEditing);
        if (!isEditing && providerCompanyData?.preferences?.offers) {
            setValue('offerExpiry', providerCompanyData.preferences.offers.officerResponseTimeHours || 0);
        }
    };

    return (
        <div className="flex items-start bg-white rounded-lg shadow-sm p-4 mb-4">
            <Tag className="text-green-500 mr-5 mt-1" style={{ fontSize: "20px" }} />
            <div className="flex-1">
                <h6 className="mb-1 text-sm font-semibold text-brand-blue-dark flex items-center">
                    Offers & Disputes
                    {!isEditing && (
                        <Pencil
                            onClick={toggleEditMode}
                            className="ml-2 w-4 h-4 cursor-pointer text-gray-500 hover:text-gray-700"
                        />
                    )}
                </h6>

                {isEditing ? (
                    <div className="mt-4">
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div className="w-full">
                                <div className="space-y-4">
                                    <div>
                                        <label className="form-label">
                                            Officer response time to new offers (expiry in hours):
                                        </label>
                                        <input
                                            className="form-input"
                                            type="number"
                                            {...register('offerExpiry')}
                                        />
                                        {errors.offerExpiry && (
                                            <span className="form-error">{errors.offerExpiry?.message}</span>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="border-t border-gray-200 my-4"></div>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="form-label"> Configure dispute offers status codes: </label>    
                                        <OfferDisputeRules rows={disputeRules} setRows={setDisputeRules} /> 
                                    </div>
                                </div>

                                <div className="flex gap-2 mt-4">
                                    <button className="px-4 py-2 bg-brand-blue-dark text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity" type="submit">
                                        Save
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                                        type="button"
                                        onClick={() => setIsEditing(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div>
                        <p className="mb-2 text-sm text-gray-600">
                            Officer response time to new offers before it expires: {providerCompanyData?.preferences?.offers?.officerResponseTimeHours || 0} hours.
                        </p>

                        <p className="mb-2 text-sm text-gray-600">
                            Offer dispute status codes: 
                        </p>

                        <div className="mt-2.5 text-sm text-gray-600">
                            <table className="w-full border-collapse">
                                <tbody>
                                    {disputeRules.map((rule, index) => (
                                        <tr key={index} className="border-b border-gray-200">
                                            <td className="py-1.5 text-left w-1/5">{rule.statusCode}</td>
                                            <td className="py-1.5 text-left w-4/5">{rule.definition}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OffersDisputes;
